import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardActionArea, CardContent, Stack } from "@mui/material";
import {
  type WorkplaceCommentAttributesType,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { parseISO } from "date-fns";

import { WorkplaceReviewCommentAuthorInfo } from "./CommentAuthorInfo";
import { WorkplaceReviewCommentLikesButton } from "./LikesButton";
import { WorkplaceReviewCommentRepliesAndLikesWrapper } from "./RepliesAndLikesWrapper";
import { ReviewContextPills } from "./ReviewContextPills";

interface WorkplaceReviewCommentCompactCardProps {
  commentId: string;
  commentText: string;
  createdAtIso: string;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  totalLikes: number;
  workerName: string;
  isLiked: boolean;
  profilePictureUrl?: string;
  isLoadingProfilePicture?: boolean;
  workplaceId: string;
  maxLines?: number;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  shiftPeriod?: WorkplaceCommentAttributesType["shiftPeriod"];
  patientsPerNurse?: string;
  shiftWorkerQualification?: string;
  rating?: number;
  useDelayedReviewLayout?: boolean;
  showEngagementActions?: boolean;
  isReadOnly?: boolean;
}

export function WorkplaceReviewCommentCompactCard(props: WorkplaceReviewCommentCompactCardProps) {
  const { onClick, isReadOnly = false } = props;

  return (
    <Card outlined variant="tertiary">
      {isReadOnly ? (
        <WorkplaceReviewCommentCompactCardContent {...props} />
      ) : (
        <CardActionArea
          sx={{
            height: "100%",
            minHeight: "11rem",
          }}
          onClick={onClick}
        >
          <WorkplaceReviewCommentCompactCardContent {...props} showEngagementActions={false} />
        </CardActionArea>
      )}
    </Card>
  );
}

interface WorkplaceReviewCommentCompactCardContentProps {
  commentId: string;
  commentText: string;
  createdAtIso: string;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  totalLikes: number;
  workerName: string;
  isLiked: boolean;
  profilePictureUrl?: string;
  isLoadingProfilePicture?: boolean;
  workplaceId: string;
  maxLines?: number;
  shiftPeriod?: WorkplaceCommentAttributesType["shiftPeriod"];
  patientsPerNurse?: string;
  shiftWorkerQualification?: string;
  rating?: number;
  useDelayedReviewLayout?: boolean;
  showEngagementActions?: boolean;
}

function WorkplaceReviewCommentCompactCardContent(
  props: WorkplaceReviewCommentCompactCardContentProps
) {
  const {
    commentId,
    commentText,
    createdAtIso,
    onLikeChanged,
    totalLikes,
    workerName,
    isLiked,
    profilePictureUrl,
    isLoadingProfilePicture,
    workplaceId,
    maxLines,
    shiftPeriod,
    patientsPerNurse,
    shiftWorkerQualification,
    rating,
    useDelayedReviewLayout,
    showEngagementActions = true,
  } = props;

  return (
    <CardContent
      sx={{
        padding: 7,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {useDelayedReviewLayout ? (
        <>
          <Stack spacing={4}>
            <WorkplaceReviewCommentAuthorInfo
              useDelayedReviewLayout
              workerName={workerName}
              profilePictureUrl={profilePictureUrl}
              isLoadingProfilePicture={isLoadingProfilePicture}
              createdAt={parseISO(createdAtIso)}
              shiftWorkerQualification={shiftWorkerQualification}
            />
            <ReviewContextPills
              shiftPeriod={shiftPeriod}
              patientsPerNurse={patientsPerNurse}
              rating={rating}
            />
            <Text
              variant="body2"
              sx={{
                ...(isDefined(maxLines) && {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: maxLines,
                  WebkitBoxOrient: "vertical",
                }),
              }}
            >
              {commentText}
            </Text>
          </Stack>

          {showEngagementActions && (
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{ marginTop: 4 }}
            >
              <WorkplaceReviewCommentRepliesAndLikesWrapper>
                <WorkplaceReviewCommentLikesButton
                  workplaceId={workplaceId}
                  commentId={commentId}
                  isLiked={isLiked}
                  count={totalLikes}
                  onLikeChanged={onLikeChanged}
                />
              </WorkplaceReviewCommentRepliesAndLikesWrapper>
            </Stack>
          )}
        </>
      ) : (
        <>
          <Text
            variant="body2"
            sx={{
              marginBottom: 6,
              ...(isDefined(maxLines) && {
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: "vertical",
              }),
            }}
          >
            {commentText}
          </Text>

          {showEngagementActions ? (
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
              <WorkplaceReviewCommentAuthorInfo
                workerName={workerName}
                profilePictureUrl={profilePictureUrl}
                isLoadingProfilePicture={isLoadingProfilePicture}
                createdAt={parseISO(createdAtIso)}
              />

              <WorkplaceReviewCommentRepliesAndLikesWrapper>
                <WorkplaceReviewCommentLikesButton
                  workplaceId={workplaceId}
                  commentId={commentId}
                  isLiked={isLiked}
                  count={totalLikes}
                  onLikeChanged={onLikeChanged}
                />
              </WorkplaceReviewCommentRepliesAndLikesWrapper>
            </Stack>
          ) : (
            <WorkplaceReviewCommentAuthorInfo
              workerName={workerName}
              profilePictureUrl={profilePictureUrl}
              isLoadingProfilePicture={isLoadingProfilePicture}
              createdAt={parseISO(createdAtIso)}
            />
          )}
        </>
      )}
    </CardContent>
  );
}
